/**
 * useProfileStatus Hook - Member Profile Status Management - By John Bellew - Infinia.ie - last updated 01/07/2025
 * 
 * Custom React hook for managing member profile completion status and disclaimer acceptance.
 * Provides comprehensive profile validation, warning management, and automatic status tracking
 * based on required fields and disclaimer acceptance. Integrates with Auth0 authentication
 * and handles profile updates with real-time status monitoring.
 * 
 * @fileoverview Custom hook for profile status management and validation
 */

import { useState, useEffect, useContext } from 'react';
import apiHelper from '../utils/apiHelper';
import { AuthContext } from '../components/Auth/AuthContext';

/**
 * Custom hook for managing member profile status and validation
 * 
 * Monitors profile completion status based on required fields (jobTitle, company,
 * memberCohort, NoOfYrsintheIndustry) and disclaimer acceptance. Provides automatic
 * status updates, warning dismissal functionality, and comprehensive profile state
 * management for the entire application.
 * 
 * @returns {Object} Hook return object with profile status and management functions
 * @returns {string|null} returns.profileStatus - Current profile status ('incomplete', 'complete', 'error', or null)
 * @returns {boolean} returns.loading - Loading state indicator for profile checks
 * @returns {Object|null} returns.profile - Complete profile data object from API
 * @returns {Function} returns.dismissWarning - Function to dismiss profile warning
 * @returns {boolean} returns.showWarning - Computed property indicating if warning should show
 * 
 * @example
 * // Basic usage for profile status checking
 * const { profileStatus, loading, showWarning } = useProfileStatus();
 * 
 * @example
 * // Advanced usage with warning dismissal
 * const { profileStatus, profile, dismissWarning, showWarning } = useProfileStatus();
 * if (showWarning) {
 *   return <ProfileWarning onDismiss={dismissWarning} />;
 * }
 * 
 * @example
 * // Profile data access with loading states
 * const { profile, loading, profileStatus } = useProfileStatus();
 * if (loading) return <Spinner />;
 * if (profileStatus === 'error') return <ErrorMessage />;
 */
export const useProfileStatus = () => {
  const { user: [user], loading: [authLoading] } = useContext(AuthContext);
  const isAuthenticated = !!user;
  const [profileStatus, setProfileStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  // Profile status checking effect
  useEffect(() => {
    /**
     * Checks profile completion status and disclaimer acceptance
     * Validates required fields and sets appropriate status based on completeness
     */
    const checkProfile = async (retryCount = 0) => {
      if (isAuthenticated && user?.profile?.email) {
        try {
          const profileData = await apiHelper.getMemberProfileByEmail(user.profile.email);

          // New user — profile doesn't exist yet, create it.
          // getMemberProfileByEmail returns null on 404 (changed for WG members
          // who can lack a portal profile); a null here means a first-time login.
          if (!profileData) {
            if (retryCount < 2) {
              const delay = (retryCount + 1) * 2000;
              setTimeout(() => {
                checkProfile(retryCount + 1);
              }, delay);
              return;
            }
            try {
              await apiHelper.createProfile({
                email: user.profile.email,
                name: user.profile.name || user.profile.email
              });
              await new Promise(resolve => setTimeout(resolve, 2000));
              const newProfile = await apiHelper.getMemberProfileByEmail(user.profile.email);
              setProfile(newProfile);
              setProfileStatus('incomplete');
            } catch (createError) {
              console.error('❌ Failed to create profile for new user:', createError);
              setProfileStatus('error');
              setProfile(null);
            }
            setLoading(false);
            return;
          }

          setProfile(profileData);

          // Check for required fields and disclaimer acceptance
          const isIncomplete = !profileData.jobTitle?.trim() ||
                               !profileData.company?.trim() ||
                               !profileData.memberCohort?.trim() ||
                               profileData.NoOfYrsintheIndustry === null ||
                               profileData.NoOfYrsintheIndustry === undefined ||
                               String(profileData.NoOfYrsintheIndustry).trim() === '';
          const disclaimerAccepted = profileData.fieldVisibility?.disclaimerAccepted === true;

          // Show disclaimer if profile incomplete OR disclaimer not accepted
          if (isIncomplete || !disclaimerAccepted) {
            setProfileStatus('incomplete');
          } else {
            setProfileStatus('complete');
          }
        } catch (error) {
          if (error.message === 'AUTH0_NOT_READY') {
            return;
          }
          console.error('❌ Profile status check error:', error);
          setProfileStatus('error');
        }
      }
      setLoading(false);
    };

    if (!authLoading) {
      checkProfile();
    }
  }, [user, isAuthenticated, authLoading]);

  /**
   * Dismisses profile warning by updating profile warning status
   * Creates profile if it doesn't exist, then sets status to complete
   */
  const dismissWarning = async () => {
    try {
      // If no profile exists, create one first
      if (!profile) {
        const newProfile = await apiHelper.createProfile({
          email: user.profile.email,
          name: user.profile.name || user.profile.email
        });
        
        // Add a small delay to allow SharePoint indexing
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // Update profile warning status
      await apiHelper.updateProfileWarning({
        email: user.profile.email
      });
      
      setProfileStatus('complete');
    } catch (error) {
      console.error('❌ Error dismissing profile warning:', error);
      // Error updating profile visibility - handled gracefully
    }
  };

  return {
    profileStatus,
    loading,
    profile,
    dismissWarning,
    showWarning: profileStatus === 'incomplete'
  };
}; 